import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import type { SessionSummary } from '../types'
import { fetchSessionList } from '../services/session-list-service'
import { toUserMessage } from '@/lib/error-messages'

export const useSessionListStore = defineStore('sessionList', () => {
  const sessions = ref<SessionSummary[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const isEmpty = computed(() => sessions.value.length === 0)
  const isCreateDialogOpen = ref(false)

  async function fetchSessions(): Promise<void> {
    isLoading.value = true
    error.value = null
    try {
      const response = await fetchSessionList()
      sessions.value = response.sessions.slice().sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      )
    } catch (err) {
      error.value = toUserMessage(err)
    } finally {
      isLoading.value = false
    }
  }

  function getSession(id: string): SessionSummary | undefined {
    return sessions.value.find((s) => s.id === id)
  }

  function updateSession(id: string, patch: Partial<SessionSummary>): void {
    const idx = sessions.value.findIndex((s) => s.id === id)
    if (idx !== -1) {
      sessions.value[idx] = Object.assign({}, sessions.value[idx], patch) as SessionSummary
    }
  }

  async function ensureLoaded(): Promise<void> {
    if (sessions.value.length > 0 || isLoading.value) return
    await fetchSessions()
  }

  return { sessions, isLoading, error, isEmpty, isCreateDialogOpen, fetchSessions, ensureLoaded, getSession, updateSession }
})
